import { useCallback, useEffect, useRef, useState } from 'react';

/**
 *
 * This is the core setup for IntersectionObservers. Use this when setting up hooks that need to use the IntersectionObserver
 *
 * @param callback - Callback invoked when the element becomes visible/not visible
 * @param ref - A React ref to the DOM element to observe
 * @param options - IntersectionObserver options (threshold, root, etc.)
 * @returns `IntersectionObserver`
 */
export const useCoreIntersectionObserver = (
  ref: React.RefObject<HTMLElement>,
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit
): void => {
  useEffect(() => {
    const observer = new IntersectionObserver(callback, {
      root: null,
      threshold: 1,
      ...options,
    });

    const target = ref.current;

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
      observer.disconnect();
    };
  }, [ref, callback, options]);
};

/**
 *
 * Tracks the visibility of a DOM element and returns a boolean (`isIntersecting`)
 * indicating whether the element is currently fully visible in the viewport.
 *
 * ✅ Use when:
 * - You want to reflect intersection status in the rendered UI (e.g., highlighting, toggling elements).
 * - You need reactivity to visibility changes within the component tree.
 *
 * 🔁 Uses useState — triggers a React rerender when intersection status changes.
 *
 * ⚠️ Caveats:
 * - Causes rerenders on intersection changes. Avoid in performance-sensitive components
 *   unless the intersection status needs to affect the UI. If you want to avoid rerenders look into using `useOnIntersect`
 * - The `onlyOnce` flag stops observing after the element is first visible, but still causes one rerender.
 * - May fire multiple times due to fine-grained intersection changes unless throttled or debounced externally.
 *
 * @param ref - A React ref to the DOM element to observe
 * @param onlyOnce - Whether to stop observing after the first intersection
 * @param options - IntersectionObserver options (threshold, root, etc.)
 * @returns `boolean` - Whether the element is currently intersecting the viewport
 */

export default function useIntersectionObserver(
  ref: React.RefObject<HTMLElement>,
  onlyOnce: boolean,
  options?: IntersectionObserverInit
): boolean {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const hasFired = useRef(false);

  const callback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      const currentlyIntersecting = entry.isIntersecting;
      if (currentlyIntersecting !== isIntersecting && (!onlyOnce || !hasFired.current)) {
        hasFired.current = true;
        setIsIntersecting(currentlyIntersecting);
      }
    },
    [isIntersecting, onlyOnce]
  );
  useCoreIntersectionObserver(ref, callback, options);

  return isIntersecting;
}
